import ReactiveState from '../../../hooks/ReactiveState';
import { FormControlState, FormGroupState } from './enums';
import { FormCommonValidatorName } from '../validators/enums';

class FormArray {
  #reactiveState;
  #validationStrategy;
  #validators;

  constructor(controls, validationStrategy, validators = []) {
    this.#validators = validators;
    this.#reactiveState = new ReactiveState({
      [FormGroupState.Controls]: controls,
      [FormGroupState.Error]: '',
      [FormGroupState.IsDisabled]: false,
    });

    this.setValidationStrategy(validationStrategy);
  }

  get value() {
    return this.controls.map((control) => control.value);
  }

  get initialValue() {
    return this.initialControls.map((control) => control.initialValue);
  }

  get controls() {
    return this.#reactiveState.value[FormGroupState.Controls];
  }

  get initialControls() {
    return this.#reactiveState.initialState[FormGroupState.Controls];
  }

  get error() {
    return this.#reactiveState.value[FormGroupState.Error];
  }

  get isDisabled() {
    return this.#reactiveState.value[FormGroupState.IsDisabled];
  }

  get isValid() {
    const areControlsValid = this.controls.every((control) => control.isValid);
    const hasFormError = !!this.error;

    return areControlsValid && !hasFormError;
  }

  get isChanged() {
    const isSomeControlChanged = this.controls.some((control) => control.isChanged);

    if (isSomeControlChanged) {
      return true;
    }

    const currentControlsLength = this.#reactiveState.value[FormGroupState.Controls]?.length;
    const initialControlsLength = this.#reactiveState.initialState[FormGroupState.Controls]?.length;

    return currentControlsLength !== initialControlsLength;
  }

  get isRequired() {
    return this.#validators.some(({ name }) => name === FormCommonValidatorName.Required);
  }

  setValidators(validators) {
    this.#validators = validators;
  }

  setValidationStrategy(formValidationStrategy) {
    this.#validationStrategy = formValidationStrategy;

    this.controls.forEach((control) => {
      control.setValidationStrategy(formValidationStrategy);
    });
  }

  addControl(control) {
    this.#reactiveState.patch({
      [FormGroupState.Controls]: [
        ...this.#reactiveState.value[FormGroupState.Controls],
        control,
      ],
    });

    control.setValidationStrategy(this.#validationStrategy);
  }

  addControls(controls) {
    controls.forEach((control) => {
      this.addControl(control);
    });
  }

  setControls(controls) {
    this.removeControls(this.controls);
    this.addControls(controls);
  }

  setInitialControls(controls) {
    this.setControls(controls);
    this.#reactiveState.patchInitialValue({
      [FormGroupState.Controls]: controls,
    });
  }

  removeControl(removingControl) {
    const newControls = this.controls.filter((control) => control !== removingControl);

    if (newControls.length === this.controls.length) {
      return;
    }

    this.#reactiveState.patch({
      [FormGroupState.Controls]: newControls,
    });
  }

  removeControls(controls) {
    controls.forEach((control) => {
      this.removeControl(control);
    });
  }

  setValue(newFormValue) {
    newFormValue?.forEach((value, index) => {
      const control = this.controls[index];

      control.setValue(value);
    });
  }

  setInitialValue(newInitialValue) {
    newInitialValue?.forEach((value, index) => {
      const control = this.controls[index];

      control.setInitialValue(value);
    });
  }

  enable() {
    this.#reactiveState.patch({ [FormGroupState.IsDisabled]: false });
    this.controls.forEach((control) => control.enable());
  }

  disable() {
    this.#reactiveState.patch({ [FormGroupState.IsDisabled]: true });
    this.controls.forEach((control) => control.disable());
  }

  reset() {
    this.controls.forEach((control) => control.reset());
  }

  clean() {
    this.controls.forEach((control) => control.clean());
  }

  setError(error) {
    this.#reactiveState.patch({
      [FormGroupState.Error]: error,
    });
  }

  setIsValid(isValid) {
    this.#reactiveState.patch({
      [FormControlState.IsValid]: isValid,
    });
  }

  getIsValid() {
    return this.controls.every((control) => control.getIsValid());
  }

  validate() {
    this.controls.forEach((control) => control.validate());

    const currentValue = this.value;
    const validatorWithError = this.#validators.find((validator) => (
      !validator.getIsValid(currentValue)
    ));

    if (!validatorWithError) {
      this.#reactiveState.resetPartially([
        FormControlState.Error,
      ]);

      return;
    }

    this.setError(validatorWithError.errorMessage);
  }
}

export default FormArray;
